import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Input,
  Textarea,
  FormLabel,
  FormControl,
  FormErrorMessage,
  Select as ChakraSelect,
  Text,
  SimpleGrid,
  Spinner,
  Switch,
  useToast,
  Badge,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import { motion } from "framer-motion";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import Select from "react-select";

const CreatePromotion = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const imageInputRef = useRef(null);

  const [formData, setFormData] = useState({
    promotion_name: "",
    promotion_description: "",
    type: "",
    value: "",
    applicable_services: [],
    start_date: "",
    end_date: "",
    code: "",
    usage_limit: "",
    is_active: false,
    image: "",
  });

  const [services, setServices] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [imageSize, setImageSize] = useState(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const pageId = 19;

  // Fetch available services
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const url = `${process.env.REACT_APP_API_URL}/services?page_id=${pageId}`;
        const data = await fetchWithToken(url);
        setServices(data.data || []);
      } catch (error) {
        toast({
          title: "Service failed to fetched.",
          status: "error",
          duration: 2500,
          isClosable: true,
          position: "top-right",
        });
      }
    };
    fetchServices();
  }, [toast, pageId]);

  // Helper for image size validation (max 1MB)
  const validateImageSize = (file) => file.size <= 1024 * 1024;

  // Convert the selected file to a base64 string with size check
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImageSize(file.size);
      if (!validateImageSize(file)) {
        setFieldErrors((prev) => ({ ...prev, image: t("Image must be less than 1MB") }));
        return;
      }
      setFieldErrors((prev) => ({ ...prev, image: undefined }));
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setFormData((prev) => ({ ...prev, image: reader.result }));
      };
    } else {
      setImageSize(null);
      setFormData((prev) => ({ ...prev, image: "" }));
    }
  };

  // Handle change for the multi-select field using react-select
  const handleServicesChange = (selectedOptions) => {
    setFormData((prev) => ({
      ...prev,
      applicable_services: selectedOptions
        ? selectedOptions.map((option) => option.value)
        : [],
    }));
  };

  // Prepare options for react-select from fetched services
  const serviceOptions = services.map((service) => ({
    value: service.name,
    label: service.name,
  }));

  const promotionTypeOptions = [
    { value: "percentage_discount", label: t("Percentage Discount") },
    { value: "fixed_discount", label: t("Fixed Discount") },
    { value: "free_delivery", label: t("Free Delivery") },
    { value: "special_offer", label: t("Special Offer") },
    { value: "bonus_tokens", label: t("Bonus Tokens") },
  ];

  // Handle changes for standard input fields
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Basic client-side validation for required fields
    if (
      !formData.promotion_name ||
      !formData.type ||
      !formData.start_date ||
      !formData.end_date ||
      (formData.type === "special_offer" && !formData.image)
    ) {
      setError(t("Please fill in all required fields."));
      setIsSubmitting(false);
      return;
    }

    const payload = {
      promotion_name: formData.promotion_name,
      promotion_description: formData.promotion_description,
      type: formData.type,
      ...(formData.value && { value: Number(formData.value) }),
      applicable_services: formData.applicable_services,
      start_date: formData.start_date,
      end_date: formData.end_date,
      code: formData.code,
      ...(formData.usage_limit && { usage_limit: Number(formData.usage_limit) }),
      is_active: formData.is_active,
    };

    if (formData.type === "special_offer" && formData.image) {
      payload.image = formData.image;
    }

    try {
      await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/promotions?page_id=${pageId}`,
        "POST",
        payload
      );

      setFormData({
        promotion_name: "",
        promotion_description: "",
        type: "",
        value: "",
        applicable_services: [],
        start_date: "",
        end_date: "",
        code: "",
        usage_limit: "",
        is_active: false,
        image: "",
      });
      setError("");
      setSuccess(true);

      // Reset the file input element
      if (imageInputRef.current) imageInputRef.current.value = "";

      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      setError(err.message || t("An error occurred. Please try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      transition={{ duration: 0.5 }}
    >
      <Card p={{ base: "20px", md: "30px" }}>
        <form onSubmit={handleSubmit}>
          {error && (
            <Text color="#FF3B3B" textAlign="center" fontSize="lg">
              {error}
            </Text>
          )}
          {success && (
            <Text color="green.500" textAlign="center">
              {t("Promotion created successfully!")}
            </Text>
          )}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 2 }} spacing={6}>
            <FormControl isRequired isInvalid={!!fieldErrors.promotion_name}>
              <FormLabel color="blue.600">{t("Promotion Name")}</FormLabel>
              <Input
                placeholder={t("Promotion Name")}
                type="text"
                name="promotion_name"
                value={formData.promotion_name}
                onChange={handleChange}
                required
              />
              <FormErrorMessage>{fieldErrors.promotion_name}</FormErrorMessage>
            </FormControl>
            <FormControl>
              <FormLabel color="blue.600">{t("Promotion Description")}</FormLabel>
              <Textarea
                placeholder={t("Promotion Description")}
                name="promotion_description"
                value={formData.promotion_description}
                onChange={handleChange}
                minH="40px"
                size="sm"
                borderRadius={"8px"}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel color="blue.600">{t("Type")}</FormLabel>
              <Select
                name="type"
                placeholder={t("Select Promotion Type")}
                options={promotionTypeOptions}
                value={promotionTypeOptions.find(option => option.value === formData.type)}
                onChange={(selectedOption) =>
                  setFormData({ ...formData, type: selectedOption.value })
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel color="blue.600">{t("Value")}</FormLabel>
              <Input
                type="number"
                name="value"
                value={formData.value}
                onChange={handleChange}
              />
            </FormControl>
            {formData.type === "special_offer" && (
              <FormControl isRequired isInvalid={!!fieldErrors.image}>
                <FormLabel color="blue.600">{t("Image")}</FormLabel>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  ref={imageInputRef}
                />
                {imageSize !== null && (
                  <Text fontSize="xs" color={imageSize > 1024 * 1024 ? "red.500" : "gray.500"} mt={1}>
                    {t("Selected file size")}: {(imageSize / 1024).toFixed(1)} KB
                    {imageSize > 1024 * 1024 && ` (${t("Too large")})`}
                  </Text>
                )}
                <FormErrorMessage>{fieldErrors.image}</FormErrorMessage>
              </FormControl>
            )}
            <FormControl>
              <FormLabel color="blue.600">{t("Applicable Services")}</FormLabel>
              <Select
                isMulti
                name="applicable_services"
                placeholder={t("Select services...")}
                options={serviceOptions}
                value={serviceOptions.filter((option) =>
                  formData.applicable_services.includes(option.value)
                )}
                onChange={handleServicesChange}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel color="blue.600">{t("Start Date")}</FormLabel>
              <Input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                required
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel color="blue.600">{t("End Date")}</FormLabel>
              <Input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                required
              />
            </FormControl>
            <FormControl>
              <FormLabel color="blue.600">{t("Promotion Code")}</FormLabel>
              <Input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleChange}
                minLength={3}
                maxLength={10}
              />
            </FormControl>
            <FormControl>
              <FormLabel color="blue.600">{t("Usage Limit")}</FormLabel>
              <Input
                type="number"
                name="usage_limit"
                value={formData.usage_limit}
                onChange={handleChange}
                min={1}
              />
            </FormControl>
            <FormControl>
              <FormLabel color="blue.600">{t("Status")}</FormLabel>
<Switch
  name="is_active"
  isChecked={formData.is_active}
  onChange={handleChange}
/>
<Badge
  colorScheme={formData.is_active ? "green" : "red"}
  variant="solid"
  ml={3}
  px={2}
  py={1}
  borderRadius="md"
  fontSize="0.85em"
>
  {formData.is_active ? t("Active") : t("Inactive")}
</Badge>
            </FormControl>
          </SimpleGrid>
          <Box>
            <Button
              mt={4}
              colorScheme="cyan"
              fontSize="m"
              borderRadius="16px"
              w={{ base: "100%", md: "148px" }}
              h="46px"
              type="submit"
              color="white"
              disabled={isSubmitting}
            >
              {isSubmitting ? <Spinner size="sm" /> : t("Create Promotion")}
            </Button>
          </Box>
          {/* Show selected image preview at the bottom */}
          {/* <Box mt={8}>
            <Text fontWeight="bold" mb={2}>{t("Selected Images")}</Text>
            <SimpleGrid columns={1} spacing={6}>
              {formData.image && (
                <Box textAlign="center">
                  <Text fontSize="sm" mb={1}>{t("Image")}</Text>
                  <img src={formData.image} alt="Image Preview" style={{ maxWidth: "100%", maxHeight: "120px", borderRadius: "8px" }} />
                </Box>
              )}
            </SimpleGrid>
          </Box> */}
        </form>
      </Card>
    </motion.div>
  );
};

export default CreatePromotion;